import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useState } from "react";
import { IHotel } from "../../../models/BaseModel";
import { Fetch } from "../../../shared/Fetch";
import { Dialog } from "primereact/dialog";
import { RoomPriceForm } from "./PriceForm";
import { addDays, format } from "date-fns";
import { Calendar } from "primereact/calendar";
import './CustomTable.css';

interface Iprops {
    open: boolean
    hideDialog: () => void
    hotel: IHotel
    toast: any
}

type MealPlanType = 'AP' | 'CP' | 'MAP' | 'EP';
interface MealPlanPrice {
    date: string | Date
    minimumStay: number
    extraAdultPrice: number
    extraChildPrice: number
    prices: {
        sharing: number
        price: number
    }[]
}
interface IRoomtypeDatePrices {
    roomTypeId: number
    roomTypeName: string
    capacity: number
    mealPlans: Partial<Record<MealPlanType, MealPlanPrice[]>>
}

export const RoomPriceTable = (props: Iprops) => {
    const [tableData, setTableData] = useState({ data: [], loading: false } as { data: IRoomtypeDatePrices[], loading: boolean });
    const [roomType, setRoomType] = useState<{ roomTypeId?: number, roomTypeName?: string, capacity?: number }>();
    const [date, setDate] = useState(new Date());
    const [expandedRows, setExpandedRows] = useState<any>([]);

    useEffect(() => {
        if (props.hotel.id) loadTableData(props.hotel.id);
    }, [props.hotel.id, date]);

    const loadTableData = async (hotelId: number) => {
        setTableData((prev) => ({ ...prev, loading: true, }));
        const endDate = addDays(date, 6);
        const dateRange = `startDate=${format(date, "yyyy-MM-dd")}&endDate=${format(endDate, "yyyy-MM-dd")}`;
        const response = await Fetch(`RoomTypePrice/${hotelId}`, "GET", null, dateRange, props.toast);
        if (response) {
            const roomTypePrice: IRoomtypeDatePrices[] = await response.json();
            setTableData({ data: roomTypePrice, loading: false });
        } else {
            setTableData((prev) => ({ ...prev, loading: false, }));
        }
    }

    const openNew = (roomTypeId: number, roomTypeName: string, capacity: number) => {
        setRoomType({ roomTypeId: roomTypeId, roomTypeName: roomTypeName, capacity: capacity });
    };

    const onRoomTypePriceUpdateSuccess = () => {
        if (props.hotel.id) loadTableData(props.hotel.id);
        setRoomType({});
    }
    const onClose = () => {
        setRoomType({});
    }

    const actionBodyTemplate = (rowData: IRoomtypeDatePrices) => {
        return (
            <div className="actions">
                <Button icon="pi pi-pencil" className='p-button-success p-button-text p-0' onClick={() => openNew(rowData.roomTypeId, rowData.roomTypeName, rowData.capacity)} />
            </div>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-4"
            style={{
                background: "linear-gradient(90deg, #def7e3ff, #ecf4edff)",
                borderBottom: "2px solid #95e99c",
                borderRadius: "8px 8px 0 0",
                padding: "0.6rem 1rem",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)"
            }}>
            <div className="flex gap-4 w-full">
                <div className="w-full flex align-items-center justify-content-center">
                    <h5 style={{ margin: 0, color: "#00796b" }}>
                        Room Prices : {props.hotel.name}
                    </h5>
                </div>
            </div>
            <div className="flex gap-4">
                <Calendar value={date} onChange={(e) => {
                    if (e.value instanceof Date) {
                        setDate(e.value);
                    } else setDate(new Date());
                }} dateFormat="dd-MM-yy" />
            </div>
        </div>
    );

    const rowExpansionTemplate = (rowData: IRoomtypeDatePrices) => {
        const mealPlans = ['AP', 'CP', 'MAP'] as const;

        return (
            <DataTable className="my_custom_clild_tbl" style={{ marginLeft: '202px' }}
                value={mealPlans.map(plan => ({
                    mealPlan: plan,
                    prices: [0, 1, 2, 3, 4, 5, 6].map(num => {
                        const thisDate = format(addDays(date, num), 'yyyy-MM-dd');
                        const planDate = rowData.mealPlans[plan]?.find(f => f.date === thisDate);
                        const price = planDate?.prices.find(p => p.sharing === rowData.capacity)?.price;
                        return { date: thisDate, price };
                    }),
                }))}
                size="small"
                stripedRows
            >
                <Column field="mealPlan" header={null} style={{ width: '100px' }} />

                {[0, 1, 2, 3, 4, 5, 6].map((num) => {
                    return (
                        <Column key={num}
                            header={null}
                            body={(data) => {
                                const thisDay = data.prices[num];
                                return thisDay?.price ?? '--';
                            }}
                            style={{ width: '140px' }}
                        />
                    );
                })}
                <Column header={null} body={''} />
            </DataTable>
        );
    };

    if (!props.open) return null;
    return (
        <div
            style={{
                backgroundColor: "#ffffff",
                border: "1px solid #a0d8a0",
                borderRadius: "12px",
                padding: "1rem",
                margin: "1rem 0",
                boxShadow: "0 4px 12px rgba(0, 128, 0, 0.1)"
            }}
        > <DataTable
            value={tableData.data}
            loading={tableData.loading}
            className="grey-teal-table my_custom_tbl"
            size="small"
            stripedRows
            showGridlines
            responsiveLayout="scroll"
            scrollHeight="70vh"
            header={header}
            rowExpansionTemplate={rowExpansionTemplate}
            expandedRows={expandedRows}
            onRowToggle={(e) => setExpandedRows(e.data)}
            rowClassName={(d: IRoomtypeDatePrices) =>
                d.roomTypeId === roomType?.roomTypeId ? 'bg-orange-100' : ''
            }
        >
                <Column expander style={{ width: '3rem' }} />
                <Column field="roomTypeName" header="Room Type" style={{ width: '160px' }} />
                <Column header="Price Type" body={'EP'} style={{ width: '100px' }} />

                {[0, 1, 2, 3, 4, 5, 6].map((num) => {
                    const showDate = format(addDays(date, num), 'dd-MM-yyy');
                    const thisDate = format(addDays(date, num), 'yyyy-MM-dd');
                    return (
                        <Column
                            key={num}
                            header={showDate}
                            body={(tr: IRoomtypeDatePrices) => {
                                const epPrice = tr.mealPlans.EP?.find(
                                    (f) => f.date === thisDate
                                )?.prices.find((f) => f.sharing === tr.capacity)?.price;
                                return epPrice ?? '--';
                            }}
                            style={{ width: '140px' }}
                        />
                    );
                })}

                <Column body={actionBodyTemplate} />
            </DataTable>
            <Dialog header={`Update (${roomType?.roomTypeName}) Price`} visible={roomType?.roomTypeId ? true : false} style={{ width: '80vw', maxWidth: '800px' }} modal onHide={onClose}>
                <RoomPriceForm roomTypeId={roomType?.roomTypeId ?? 0} capacity={roomType?.capacity ?? 0} onSuccess={onRoomTypePriceUpdateSuccess} onClose={onClose} toast={props.toast} />
            </Dialog>
        </div>
    );
}
